import React, { useState } from "react";
import { Airplane, Warning, Coins } from "@phosphor-icons/react";
import { formatCurrency } from "../utils/calculations";
import { Asset } from "../types";

interface RunwayCardProps {
  currentRunway: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  currentBalance: number;
  assets: Asset[];
  showSeizedScenario?: boolean;
  onToggleSeizedScenario?: (show: boolean) => void;
}

const RunwayCard: React.FC<RunwayCardProps> = ({
  currentRunway,
  monthlyIncome,
  monthlyExpenses,
  currentBalance,
  assets,
  showSeizedScenario = false,
  onToggleSeizedScenario,
}) => {
  const [internalShowSeizedScenario, setInternalShowSeizedScenario] =
    useState(false);

  // Use external state if provided, otherwise use internal state
  const isSeizedScenario = onToggleSeizedScenario
    ? showSeizedScenario
    : internalShowSeizedScenario;
  const setSeizedScenario =
    onToggleSeizedScenario || setInternalShowSeizedScenario;

  const calculateSeizedRunway = () => {
    if (monthlyExpenses <= 0) return Infinity;
    return currentBalance / monthlyExpenses;
  };

  const seizedRunway = calculateSeizedRunway();
  const liquidAssets = assets
    .filter((a: Asset) => a.isLiquid)
    .reduce((sum: number, a: Asset) => sum + a.balance, 0);
  const totalAssets = assets.reduce(
    (sum: number, a: Asset) => sum + a.balance,
    0
  );

  const formatRunway = (runway: number) => {
    if (runway === Infinity || runway === -Infinity) {
      return monthlyExpenses <= 0 ? "No expenses" : "∞ months";
    }
    if (isNaN(runway)) {
      return "0.0 months";
    }
    return `${runway.toFixed(1)} months`;
  };

  const getRunwayColor = (runway: number) => {
    if (monthlyExpenses <= 0) return "text-gray-500";
    if (runway >= 12) return "text-green-500";
    if (runway >= 6) return "text-yellow-500";
    if (runway >= 3) return "text-orange-500";
    return "text-red-500";
  };

  const getRunwayMessage = (runway: number) => {
    if (monthlyExpenses <= 0) return "Add expenses to see your runway";
    if (runway >= 12) return "Excellent runway";
    if (runway >= 6) return "Good runway";
    if (runway >= 3) return "Limited runway";
    return "Critical runway";
  };

  const getZeroDate = (runway: number) => {
    if (
      runway === Infinity ||
      runway === -Infinity ||
      isNaN(runway) ||
      monthlyExpenses <= 0
    ) {
      return null;
    }

    const now = new Date();
    const monthsToAdd = Math.ceil(runway);
    const zeroDate = new Date(
      now.getFullYear(),
      now.getMonth() + monthsToAdd,
      now.getDate()
    );

    return zeroDate;
  };

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
        <div className="flex items-center space-x-2">
          <Airplane className="w-6 h-6 text-primary-500" weight="duotone" />
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {isSeizedScenario ? "Runway (Income Seized)" : "Monthly Runway"}
          </h3>
        </div>
        {monthlyIncome > 0 && (
          <button
            onClick={() => setSeizedScenario(!isSeizedScenario)}
            className={`text-xs px-3 py-2 rounded transition-colors w-full sm:w-auto ${
              isSeizedScenario
                ? "bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-600/50"
                : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
            }`}>
            {isSeizedScenario ? "Show Normal" : "Show Seized"}
          </button>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-center space-x-2">
            <p
              className={`text-2xl font-bold ${getRunwayColor(
                isSeizedScenario ? seizedRunway : currentRunway
              )}`}>
              {formatRunway(isSeizedScenario ? seizedRunway : currentRunway)}
            </p>
            {getZeroDate(isSeizedScenario ? seizedRunway : currentRunway) && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                (
                {(
                  getZeroDate(
                    isSeizedScenario ? seizedRunway : currentRunway
                  ) as Date
                ).toLocaleDateString("en-US", {
                  month: "short",
                  year: "numeric",
                })}
                )
              </p>
            )}
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            {getRunwayMessage(isSeizedScenario ? seizedRunway : currentRunway)}
          </p>
        </div>

        {/* Asset Information */}
        {assets.length > 0 && (
          <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Coins className="w-4 h-4 text-primary-400" weight="duotone" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Assets
              </span>
            </div>
            <div className="text-xs text-gray-700 dark:text-gray-400 space-y-1">
              <p>• Total assets: {formatCurrency(totalAssets)}</p>
              <p>• Liquid assets: {formatCurrency(liquidAssets)}</p>
              <p>• Asset count: {assets.length}</p>
            </div>
          </div>
        )}

        {isSeizedScenario && monthlyIncome > 0 && (
          <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Warning className="w-4 h-4 text-red-400" weight="duotone" />
              <span className="text-sm font-medium text-red-400">
                Income Seized Scenario
              </span>
            </div>
            <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
              <p>• No new income starting today</p>
              <p>• Living off current balance only</p>
              <p>
                • Monthly expenses:{" "}
                {monthlyExpenses > 0
                  ? formatCurrency(monthlyExpenses)
                  : "No expenses recorded"}
              </p>
              <p>• Current balance: {formatCurrency(currentBalance)}</p>
              {liquidAssets > 0 && (
                <p>• Liquid assets: {formatCurrency(liquidAssets)}</p>
              )}
            </div>
          </div>
        )}

        {!isSeizedScenario && monthlyIncome > 0 && (
          <div className="text-xs text-gray-700 dark:text-gray-300">
            <p>With {formatCurrency(monthlyIncome)} monthly income</p>
            {monthlyExpenses <= 0 && (
              <p className="mt-1 text-orange-500">
                Add expenses to calculate your runway
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RunwayCard;
